import React, { useEffect, useState } from 'react';
import { SettingsState } from '../../../organisms/SelectSettings';
import Grid from '@material-ui/core/Grid';
import PLMSettingGrid from './PLMSettingGrid';
import PLMSettingFooter from './PLMSettingFooter';
import ConfirmDialog, { ConfirmDialogResponse } from '../../../atoms/Dialog/ConfirmDialog';
import MessageDialog, { MessageDialogResponse } from '../../../atoms/Dialog/MessageDialog';
import { useModal } from '../../../../function/useModal';
import { ResponceDataType, ErrorCode } from '../../../../ErrorCode';
import { PLM_CSV_SET, GetPLMSettings, CreatePLMSetting, CheckPLMSettingInputData, PutPLMSettings } from './PLMSetting';
import { pmlSettingPageStyle } from './PLMSettingStyle';
import { useTranslation } from 'react-i18next';

type PLMSettingPageProps = { editData: SettingsState };
type SaveParameter = {
  plmSettingList: PLM_CSV_SET[];
  plmDeleteIndexList: number[];
};

/**
 *PLM連携画面
 * @returns
 */
const PLMSettingPage: React.FC<PLMSettingPageProps> = (props) => {
  const { t } = useTranslation();

  const [earlyPlmListCount, setEarlyPlmListCount] = useState<number>(0); //初期データ数からデータ件数全行削除された場合での編集有り無し判定で使用
  const [plmSettingList, setPLMSettingList] = useState<PLM_CSV_SET[]>([]);
  const [plmDeleteIndexList, setPLMDeleteIndexList] = useState<number[]>([]);
  const [flgCfmDlg, actCfmDlg] = useModal<ConfirmDialogResponse>();
  const [flgMsgDlg, actMsgDlg] = useModal<MessageDialogResponse>();

  const [dlgmsg, setDlgmsg] = useState<string>('');
  const [buttonEnable, setButtonEnable] = useState<boolean>(true);

  const cfmdlg = flgCfmDlg ? (
    <ConfirmDialog
      dialog={actCfmDlg}
      message={dlgmsg}
      action={t('PLMSettingPage.はい', 'はい')}
      cancel={t('PLMSettingPage.いいえ', 'いいえ')}
    />
  ) : null;

  const msgdlg = flgMsgDlg ? <MessageDialog dialog={actMsgDlg} message={dlgmsg} /> : null;

  useEffect(() => {
    props.editData.SaveFunction = saveAction2;
    props.editData.IsUpdate = false;
  }, [props.editData.CancelCondition]);

  /**
   * 初期表示　useEffect
   */
  useEffect(() => {
    upDatePage();
  }, []);

  /**
   * 画面更新
   */
  const upDatePage = () => {
    setButtonEnable(false);
    let plmList: PLM_CSV_SET[] = [];
    GetPLMSettings()
      .then((r: PLM_CSV_SET[]) => {
        console.debug(['PLMSettingPage - upDatePage - GetPLMSettings', r]);
        plmList = r;
        setEarlyPlmListCount(r.length);
        setPLMSettingList(r);
        setPLMDeleteIndexList([]);
        props.editData.IsUpdate = false;
      })
      .catch((e) => {
        setDlgmsg(t('PLMSettingPage.データ取得中にエラーが発生しました', 'データ取得中にエラーが発生しました'));
        actMsgDlg.open();
        console.error(['upDatePage - Error', e, plmList]);
        setEarlyPlmListCount(0);
        setPLMSettingList([]);
        setPLMDeleteIndexList([]);
      })
      .finally(() => {
        setButtonEnable(true);
      });
  };

  /**
   *新規ボタン押下
   * @returns
   */
  const newAction = () => {
    if (plmSettingList.length === 5) {
      setDlgmsg(t('PLMSettingPage.設定はサーバ５台までです', '設定はサーバ５台までです。'));
      actMsgDlg.open();
      return;
    }
    let newRow = CreatePLMSetting();
    let maxIndex = 1;
    if (plmSettingList.length != 0) {
      maxIndex = Math.max(...plmSettingList.map((p) => p.indexNo));
      maxIndex = maxIndex + 1;
    }
    newRow.indexNo = maxIndex;
    plmSettingList.push(newRow);
    setPLMSettingList([...plmSettingList]);
    setParameterTabChange({
      plmSettingList: plmSettingList,
      plmDeleteIndexList: plmDeleteIndexList,
    });
  };

  /**
   * 更新ボタン押下
   */
  const refreshAction = () => {
    console.debug(['refreshAction', plmSettingList]);
    if (plmSettingList.filter((r) => r.isEdit).length !== 0 || plmSettingList.length !== earlyPlmListCount) {
      setDlgmsg(
        t(
          'PLMSettingPage.保存していないデータを破棄します。\nよろしいですか?',
          '保存していないデータを破棄します。\nよろしいですか?'
        )
      );
      actCfmDlg
        .open()
        .apply((_) => {
          upDatePage(); //再描画
          console.debug(t('PLMSettingPage.実行', '実行'));
          props.editData.IsUpdate = false;
        })
        .cancel(() => {
          console.debug(t('PLMSettingPage.キャンセル', 'キャンセル'));
        });
    } else {
      upDatePage(); //再描画
      console.debug(t('PLMSettingPage.実行', '実行'));
      props.editData.IsUpdate = false;
    }
  };

  /**
   *保存ボタン
   * @param index
   */
  const saveAction = () => {
    const messages = CheckPLMSettingInputData(plmSettingList, t);
    if (messages.length != 0) {
      setDlgmsg(messages.join('\n'));
      actMsgDlg.open();
      return;
    }

    if (plmSettingList.filter((r) => r.isEdit).length !== 0 || plmSettingList.length !== earlyPlmListCount) {
      setDlgmsg(t('PLMSettingPage.編集内容を保存します。\nよろしいですか?', '編集内容を保存します。\nよろしいですか?'));
      actCfmDlg
        .open()
        .apply((_) => {
          setButtonEnable(false);
          PutPLMSettings(
            plmSettingList.filter((r) => r.isEdit),
            plmDeleteIndexList
          )
            .then((r) => {
              setDlgmsg(t('PLMSettingPage.保存が正常に終了しました', '保存が正常に終了しました'));
              actMsgDlg.open();
              upDatePage(); //再描画
              console.debug('saveAction ok');
              props.editData.IsUpdate = false;
            })
            .catch((r) => {
              setDlgmsg(t('PLMSettingPage.保存中にエラーが発生しました', '保存中にエラーが発生しました'));
              actMsgDlg.open();
              console.error(['saveAction error', r]);
            })
            .finally(() => {
              setButtonEnable(true);
            });
        })
        .cancel(() => {
          console.debug(t('PLMSettingPage.キャンセル', 'キャンセル'));
        });
    } else {
    }
  };

  /**
   * タブ切替時に動作
   * @param param
   * @returns
   */
  const saveAction2 = (param: object): Promise<string> => {
    return new Promise((resolve, reject) => {
      const sp = param as SaveParameter;
      const messages = CheckPLMSettingInputData(sp.plmSettingList, t);
      if (messages.length != 0) {
        setDlgmsg(messages.join('\n'));
        actMsgDlg.open();
        reject('data error');
        return;
      }
      PutPLMSettings(
        sp.plmSettingList.filter((r) => r.isEdit),
        sp.plmDeleteIndexList
      )
        .then((r) => {
          console.debug('saveAction ok');
          props.editData.IsUpdate = false;
          resolve('done');
        })
        .catch((r) => {
          setDlgmsg(t('PLMSettingPage.保存中にエラーが発生しました', '保存中にエラーが発生しました'));
          actMsgDlg.open();
          console.error(['saveAction error', r]);
          reject('data error');
        })
        .finally(() => {
          setButtonEnable(true);
        });
    });
  };

  /**
   *削除ボタン押下
   * @param index
   */
  const deleteAction = (index: number) => {
    console.debug(['deleteAction - start', plmSettingList, index]);
    setDlgmsg(t('PLMSettingPage.削除してもよろしいですか', '削除してもよろしいですか'));
    actCfmDlg
      .open()
      .apply((_) => {
        //削除対象抜き出し
        const delRow = plmSettingList.filter((row) => row.indexNo === index);
        //削除対象を抜いたリスト
        let newList = plmSettingList.filter((row) => row.indexNo !== index);
        //連番振りなおし
        newList.forEach((r, index) => (r.indexNo = index + 1));

        if (delRow.length !== 0) {
          //削除リスト追加
          if (delRow[0].PLM_INDEX !== null && delRow[0].PLM_INDEX !== 0) {
            plmDeleteIndexList.push(delRow[0].PLM_INDEX);
            setPLMDeleteIndexList([...plmDeleteIndexList]);
          }
        }
        setPLMSettingList([...newList]);
        setParameterTabChange({
          plmSettingList: newList,
          plmDeleteIndexList: plmDeleteIndexList,
        });
        console.debug(['deleteAction - end newList , index , deleteList ', newList, index, plmDeleteIndexList]);
      })
      .cancel(() => {
        console.debug(t('PLMSettingPage.キャンセル', 'キャンセル'));
      });
  };

  /**
   *キャンセルボタン押下
   */
  const cancelAction = () => {
    console.debug(['cancelAction', plmSettingList]);
    if (plmSettingList.filter((r) => r.isEdit).length !== 0 || plmSettingList.length !== earlyPlmListCount) {
      setDlgmsg(
        t(
          'PLMSettingPage.保存していないデータを破棄します。\nよろしいですか?',
          '保存していないデータを破棄します。\nよろしいですか?'
        )
      );
      actCfmDlg
        .open()
        .apply((_) => {
          upDatePage(); //再描画
          console.debug(t('PLMSettingPage.実行', '実行'));
          props.editData.IsUpdate = false;
        })
        .cancel(() => {
          console.debug(t('PLMSettingPage.キャンセル', 'キャンセル'));
        });
    } else {
    }
  };

  /**
   *データ変更通知
   * @returns
   */
  const changeAction = (target: PLM_CSV_SET) => {
    console.debug(['changeAction - start', plmSettingList, target]);
    target.isEdit = true;
    var index = plmSettingList.findIndex((r) => r.indexNo === target.indexNo);
    plmSettingList[index] = target;
    //setPLMSettingList([...plmSettingList]);
    setParameterTabChange({
      plmSettingList: plmSettingList,
      plmDeleteIndexList: plmDeleteIndexList,
    });
    console.debug(['changeAction - end', plmSettingList, target]);
  };

  /**
   * タブ切替時の処理パラーメータをセット
   */
  const setParameterTabChange = (tmp: SaveParameter) => {
    props.editData.Target = tmp;
    props.editData.IsUpdate = true;
    console.debug(['setParameterTabChange -  ', tmp]);
  };

  return (
    <Grid container justify="flex-start" spacing={1} direction="row" alignItems="center" style={{ margin: 2 }}>
      <Grid item xs={12}>
        <PLMSettingGrid
          plmSettingList={plmSettingList}
          onClickDeleteButton={deleteAction}
          onChangeData={changeAction}
        ></PLMSettingGrid>
      </Grid>
      <Grid item xs={12}>
        <PLMSettingFooter
          onClickSaveButton={saveAction}
          onClickCancelButton={cancelAction}
          onClickNewButton={newAction}
          onClickRefreshButton={refreshAction}
          buttonEnable={buttonEnable}
        ></PLMSettingFooter>
      </Grid>
      {cfmdlg}
      {msgdlg}
    </Grid>
  );
};

export default PLMSettingPage;
